if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      static addToCartQueue = Promise.resolve();

      static enqueueAdd(operation) {
        const queuedOperation = ProductForm.addToCartQueue.then(operation, operation);
        ProductForm.addToCartQueue = queuedOperation.catch(() => undefined);
        return queuedOperation;
      }

      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const formData = new FormData(this.form);
        const activeElement = document.activeElement;

        ProductForm.enqueueAdd(() => this.submitToCart(evt, formData, activeElement));
      }

      async submitToCart(evt, formData, activeElement) {
        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        let linesUpdateDeferred;

        try {
          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];

          if (this.cart) {
            formData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
            this.cart.setActiveElement(activeElement);
          }
          config.body = formData;
          linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

          const fetchResponse = await fetch(`${routes.cart_add_url}`, config);
          const response = await fetchResponse.json();

          if (response.status) {
            const errorPublication = this.publishSafely(PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId: variantId,
              errors: response.errors || response.description,
              message: response.message,
            });
            this.handleErrorMessage(response.description);
            this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
            linesUpdateDeferred?.reject(new Error(response.description || response.message));

            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
            if (soldOutMessage) {
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
            }

            await errorPublication;
            return;
          } else if (!this.cart) {
            this.resolveCartLinesUpdate(linesUpdateDeferred);
            window.location = window.routes.cart_url;
            return;
          }

          const linesUpdateResolution = this.resolveCartLinesUpdate(linesUpdateDeferred);

          const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
          const shouldPublishUpdate = !this.error;
          const updatePublication = shouldPublishUpdate
            ? this.publishSafely(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              })
            : Promise.resolve();
          this.error = false;

          await this.renderCartContents(response);
          await updatePublication;
          if (shouldPublishUpdate) {
            CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
          }
          await linesUpdateResolution;
        } catch (e) {
          console.error(e);
          const errorMessage = window.cartStrings?.error || '';
          this.handleErrorMessage(errorMessage);
          const errorPublication = this.publishSafely(PUB_SUB_EVENTS.cartError, {
            source: 'product-form',
            productVariantId: variantId,
            errors: errorMessage,
            message: errorMessage,
          });
          this.dispatchCartErrorEvent(errorMessage, 'SERVICE_UNAVAILABLE');
          linesUpdateDeferred?.reject(e);
          await errorPublication;
        } finally {
          this.submitButton.classList.remove('loading');
          if (!this.error) this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading__spinner').classList.add('hidden');

          CartPerformance.measureFromEvent("add:user-action", evt);
        }
      }

      async publishSafely(eventName, payload) {
        try {
          await publish(eventName, payload);
        } catch (e) {
          console.error(e);
        }
      }

      async renderCartContents(response) {
        const renderContents = () => {
          CartPerformance.measure("add:paint-updated-sections", () => {
            this.cart.renderContents(response);
          });
        };
        const quickAddModal = this.closest('quick-add-modal');

        if (!quickAddModal) {
          renderContents();
          return;
        }

        await new Promise((resolve, reject) => {
          const handleModalClosed = () => {
            setTimeout(() => {
              try {
                renderContents();
                resolve();
              } catch (e) {
                reject(e);
              }
            });
          };

          document.body.addEventListener('modalClosed', handleModalClosed, { once: true });

          try {
            quickAddModal.hide(true);
          } catch (e) {
            document.body.removeEventListener('modalClosed', handleModalClosed);
            reject(e);
          }
        });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return Promise.resolve();
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return Promise.resolve();

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        return pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
